import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, startOfDay, subMonths, eachDayOfInterval, endOfDay } from "date-fns";
import { getAllMetrics } from "@/lib/metricDefinitions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface CompareGraphsProps {
  userId: string;
}

interface DailyEntry {
  entry_date: string;
  [key: string]: any;
}

interface CustomMetric {
  metric_key: string;
  metric_name: string;
  metric_emoji: string;
  metric_unit: string;
  metric_type?: "numeric" | "boolean";
}

const defaultColors = {
  0: "rgb(229 231 235)", // gray-200
  1: "rgb(134 239 172)", // green-300
  2: "rgb(74 222 128)", // green-400
  3: "rgb(34 197 94)", // green-500
  4: "rgb(22 163 74)", // green-600
};

const getIntensity = (value: number | null, goal: number): number => {
  if (!value) return 0;
  const percentage = (value / goal) * 100;
  if (percentage >= 100) return 4;
  if (percentage >= 75) return 3;
  if (percentage >= 50) return 2;
  if (percentage > 0) return 1;
  return 0;
};

const CompareGraphs = ({ userId }: CompareGraphsProps) => {
  const { t } = useTranslation();
  const [metric1, setMetric1] = useState<string>("");
  const [metric2, setMetric2] = useState<string>("");
  const [correlationView, setCorrelationView] = useState<"traditional" | "metric-split">("traditional");
  
  // Calculate date range (3 months back)
  const endDate = endOfDay(new Date());
  const startDate = startOfDay(subMonths(new Date(), 3));
  
  // Fetch all available metrics
  const { data: allMetrics = [] } = useQuery({
    queryKey: ["all-metrics"],
    queryFn: getAllMetrics,
  });

  // Fetch custom metrics
  const { data: customMetrics = [] } = useQuery({
    queryKey: ["custom-metrics", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_metrics")
        .select("*")
        .eq("user_id", userId)
        .order("metric_name");
      
      if (error) throw error;
      return data as CustomMetric[];
    },
    enabled: !!userId,
  });

  // Fetch metric preferences to filter by enabled metrics
  const { data: metricPreferences } = useQuery({
    queryKey: ["metric-preferences", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("metric_preferences")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Fetch active metrics order from chart preferences
  const { data: chartPreferencesData } = useQuery({
    queryKey: ["chart-preferences-active", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_chart_preferences")
        .select("active_metrics")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (error && error.code !== "PGRST116") throw error;
      return data?.active_metrics || null;
    },
    enabled: !!userId,
  });

  // Build list of enabled metric keys from preferences
  const enabledMetricKeys = new Set<string>();
  
  if (metricPreferences) {
    // Add metrics from quick edit preferences
    if (metricPreferences.nutrition_metrics) {
      metricPreferences.nutrition_metrics.forEach((m: string) => enabledMetricKeys.add(m));
    }
    if (metricPreferences.activity_sleep_metrics) {
      metricPreferences.activity_sleep_metrics.forEach((m: string) => enabledMetricKeys.add(m));
    }
    if (metricPreferences.results_metrics) {
      metricPreferences.results_metrics.forEach((m: string) => enabledMetricKeys.add(m));
    }
  }

  // Add active metrics from chart preferences
  if (chartPreferencesData && Array.isArray(chartPreferencesData)) {
    chartPreferencesData.forEach(key => enabledMetricKeys.add(key));
  }

  // Add all custom metrics (they're enabled by being created)
  customMetrics.forEach(m => enabledMetricKeys.add(m.metric_key));

  // Filter metrics to only show enabled ones
  const allAvailableMetrics = [
    ...allMetrics.map(m => ({ key: m.key, label: `${m.emoji} ${m.label}`, emoji: m.emoji, name: m.label })),
    ...customMetrics.map(m => ({ key: m.metric_key, label: `${m.metric_emoji} ${m.metric_name}`, emoji: m.metric_emoji, name: m.metric_name }))
  ];

  // Only show metrics that are enabled
  const availableMetrics = allAvailableMetrics.filter(m => enabledMetricKeys.has(m.key));

  // Fetch daily entries
  const { data: entries = [] } = useQuery({
    queryKey: ["compare-entries", userId, startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("daily_entries")
        .select("*")
        .eq("user_id", userId)
        .gte("entry_date", format(startDate, "yyyy-MM-dd"))
        .lte("entry_date", format(endDate, "yyyy-MM-dd"))
        .order("entry_date", { ascending: true });
      
      if (error) throw error;
      return data as DailyEntry[];
    },
    enabled: !!userId,
  });

  // Fetch user goals
  const { data: goals } = useQuery({
    queryKey: ["user-goals", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_goals")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  // Create entry lookup
  const entryMap = new Map(entries.map(e => [e.entry_date, e]));
  
  // Generate all days in range
  const allDays = eachDayOfInterval({ start: startDate, end: endDate });
  
  // Organize into weeks
  const weeks: Array<Array<{ date: Date; entry: DailyEntry | null }>> = [];
  let currentWeek: Array<{ date: Date; entry: DailyEntry | null }> = [];
  
  allDays.forEach((date) => {
    const dayOfWeek = date.getDay();
    
    if (dayOfWeek === 0 && currentWeek.length > 0) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    
    const dateStr = format(date, "yyyy-MM-dd");
    currentWeek.push({ date, entry: entryMap.get(dateStr) || null });
  });
  
  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  // Helper to get value from entry
  const getValueFromEntry = (entry: DailyEntry | null, metricKey: string): number | null => {
    if (!entry) return null;
    const valueKey = metricKey.endsWith('_value') ? metricKey : `${metricKey}`;
    return entry[valueKey] ?? entry[`${metricKey}_value`] ?? null;
  };

  // Calculate correlation
  const calculateCorrelation = () => {
    if (!metric1 || !metric2) return null;

    const pairs: Array<[number, number]> = [];
    
    entries.forEach(entry => {
      const val1 = getValueFromEntry(entry, metric1);
      const val2 = getValueFromEntry(entry, metric2);
      
      if (val1 !== null && val2 !== null) {
        pairs.push([val1, val2]);
      }
    });

    if (pairs.length < 2) return null;

    // Calculate Pearson correlation coefficient
    const n = pairs.length;
    const sum1 = pairs.reduce((sum, [v1]) => sum + v1, 0);
    const sum2 = pairs.reduce((sum, [, v2]) => sum + v2, 0);
    const sum1Sq = pairs.reduce((sum, [v1]) => sum + v1 * v1, 0);
    const sum2Sq = pairs.reduce((sum, [, v2]) => sum + v2 * v2, 0);
    const pSum = pairs.reduce((sum, [v1, v2]) => sum + v1 * v2, 0);

    const num = pSum - (sum1 * sum2 / n);
    const den = Math.sqrt((sum1Sq - sum1 * sum1 / n) * (sum2Sq - sum2 * sum2 / n));

    if (den === 0) return null;

    return num / den;
  };

  const correlation = calculateCorrelation();

  // Get goal values
  const getGoalValue = (metricKey: string) => {
    const goalKey = `${metricKey}_goal` as keyof typeof goals;
    const metricInfo = allMetrics.find(m => m.key === metricKey);
    return goals?.[goalKey] as number || metricInfo?.defaultGoal || 100;
  };

  // Render mini graph for a metric
  const renderMiniGraph = (metricKey: string) => {
    if (!metricKey) return null;

    const metricInfo = availableMetrics.find(m => m.key === metricKey);
    const goalValue = getGoalValue(metricKey);

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>{metricInfo?.emoji}</span>
          <span>{metricInfo?.name}</span>
        </div>
        <TooltipProvider>
          <div className="flex gap-1">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="flex flex-col gap-1">
                {week.map((day, dayIndex) => {
                  const value = getValueFromEntry(day.entry, metricKey);
                  const intensity = getIntensity(value, goalValue);
                  const bgColor = defaultColors[intensity as keyof typeof defaultColors];
                  
                  return (
                    <Tooltip key={dayIndex}>
                      <TooltipTrigger>
                        <div
                          className="w-3 h-3 rounded-sm transition-opacity hover:opacity-80"
                          style={{ backgroundColor: bgColor }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs font-medium">{format(day.date, "MMM d, yyyy")}</p>
                        <p className="text-xs">Value: {value ?? 0}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            ))}
          </div>
        </TooltipProvider>
      </div>
    );
  };

  // Correlation interpretation
  const getCorrelationInterpretation = (corr: number | null) => {
    if (corr === null) return { text: "Not enough data", icon: Minus, color: "text-muted-foreground" };
    
    const absCorr = Math.abs(corr);
    const isPositive = corr > 0;
    
    if (absCorr >= 0.7) {
      return {
        text: isPositive ? "Strong Positive Correlation" : "Strong Negative Correlation",
        icon: isPositive ? TrendingUp : TrendingDown,
        color: isPositive ? "text-green-500" : "text-red-500",
        description: isPositive 
          ? "When one metric increases, the other tends to increase significantly."
          : "When one metric increases, the other tends to decrease significantly."
      };
    } else if (absCorr >= 0.4) {
      return {
        text: isPositive ? "Moderate Positive Correlation" : "Moderate Negative Correlation",
        icon: isPositive ? TrendingUp : TrendingDown,
        color: isPositive ? "text-green-400" : "text-orange-400",
        description: isPositive 
          ? "There's a noticeable tendency for both metrics to move together."
          : "There's a noticeable tendency for metrics to move in opposite directions."
      };
    } else if (absCorr >= 0.2) {
      return {
        text: isPositive ? "Weak Positive Correlation" : "Weak Negative Correlation",
        icon: isPositive ? TrendingUp : TrendingDown,
        color: isPositive ? "text-blue-400" : "text-yellow-400",
        description: isPositive 
          ? "A slight tendency for both metrics to move together."
          : "A slight tendency for metrics to move in opposite directions."
      };
    } else {
      return {
        text: "Little to No Correlation",
        icon: Minus,
        color: "text-muted-foreground",
        description: "These metrics appear to be independent of each other."
      };
    }
  };

  const interpretation = correlation !== null ? getCorrelationInterpretation(correlation) : null;
  const Icon = interpretation?.icon || Minus;

  // Render visual correlation graph
  const renderCorrelationGraph = () => {
    if (!metric1 || !metric2) return null;

    const goalValue1 = getGoalValue(metric1);
    const goalValue2 = getGoalValue(metric2);
    const metric1Info = availableMetrics.find(m => m.key === metric1);
    const metric2Info = availableMetrics.find(m => m.key === metric2);

    if (correlationView === "metric-split") {
      // New view: 4-color system showing which metrics were met
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <span>{t('ui.text.metric_achievement')}</span>
          </div>
          <TooltipProvider>
            <div className="flex gap-1">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="flex flex-col gap-1">
                  {week.map((day, dayIndex) => {
                    const val1 = getValueFromEntry(day.entry, metric1);
                    const val2 = getValueFromEntry(day.entry, metric2);

                    let bgColor = "rgb(148 163 184)"; // slate-400 (neither - no data or both not met)
                    let statusText = "Neither met";

                    if (val1 !== null && val2 !== null) {
                      const percent1 = getIntensity(val1, goalValue1);
                      const percent2 = getIntensity(val2, goalValue2);

                      const metric1Met = percent1 >= 3; // >= 75% of goal
                      const metric2Met = percent2 >= 3; // >= 75% of goal

                      if (metric1Met && metric2Met) {
                        bgColor = "rgb(139 92 246)"; // violet-500 (both metrics met)
                        statusText = "Both met";
                      } else if (metric1Met) {
                        bgColor = "rgb(59 130 246)"; // blue-500 (metric 1 only)
                        statusText = `${metric1Info?.name} met`;
                      } else if (metric2Met) {
                        bgColor = "rgb(236 72 153)"; // pink-500 (metric 2 only)
                        statusText = `${metric2Info?.name} met`;
                      }
                    } else if (val1 !== null) {
                      const percent1 = getIntensity(val1, goalValue1);
                      if (percent1 >= 3) {
                        bgColor = "rgb(59 130 246)"; // blue-500 (metric 1 only)
                        statusText = `${metric1Info?.name} met`;
                      }
                    } else if (val2 !== null) {
                      const percent2 = getIntensity(val2, goalValue2);
                      if (percent2 >= 3) {
                        bgColor = "rgb(236 72 153)"; // pink-500 (metric 2 only)
                        statusText = `${metric2Info?.name} met`;
                      }
                    }

                    return (
                      <Tooltip key={dayIndex}>
                        <TooltipTrigger>
                          <div
                            className="w-3 h-3 rounded-sm transition-opacity hover:opacity-80"
                            style={{ backgroundColor: bgColor }}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs font-medium">{format(day.date, "MMM d, yyyy")}</p>
                          <p className="text-xs">{metric1Info?.name}: {val1 ?? 0}</p>
                          <p className="text-xs">{metric2Info?.name}: {val2 ?? 0}</p>
                          <p className="text-xs font-semibold mt-1">{statusText}</p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              ))}
            </div>
          </TooltipProvider>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-xs text-muted-foreground mt-2">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-blue-500" />
              <span>{metric1Info?.emoji} Only</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-pink-500" />
              <span>{metric2Info?.emoji} Only</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-violet-500" />
              <span>{t('compare.legend_both')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-slate-400" />
              <span>{t('compare.legend_neither')}</span>
            </div>
          </div>
        </div>
      );
    }

    // Traditional view: correlation-based coloring
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span>{t('ui.text.correlation_visualization')}</span>
        </div>
        <TooltipProvider>
          <div className="flex gap-1">
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="flex flex-col gap-1">
                {week.map((day, dayIndex) => {
                  const val1 = getValueFromEntry(day.entry, metric1);
                  const val2 = getValueFromEntry(day.entry, metric2);

                  let bgColor = "rgb(229 231 235)"; // gray-200 (no data)
                  let statusText = "No data";

                  if (val1 !== null && val2 !== null) {
                    const percent1 = getIntensity(val1, goalValue1);
                    const percent2 = getIntensity(val2, goalValue2);

                    // Both high = positive correlation (green)
                    // Both low = positive correlation (green)
                    // One high, one low = negative correlation (red)
                    // Mixed = neutral (gray)

                    const both_high = percent1 >= 3 && percent2 >= 3;
                    const both_low = percent1 <= 1 && percent2 <= 1;
                    const opposite = (percent1 >= 3 && percent2 <= 1) || (percent1 <= 1 && percent2 >= 3);

                    if (both_high || both_low) {
                      bgColor = "rgb(34 197 94)"; // green-500 (positive)
                      statusText = t('compare.correlation_strong_positive');
                    } else if (opposite) {
                      bgColor = "rgb(239 68 68)"; // red-500 (negative)
                      statusText = t('compare.correlation_strong_negative');
                    } else {
                      bgColor = "rgb(156 163 175)"; // gray-400 (neutral)
                      statusText = t('compare.legend_neutral');
                    }
                  }

                  return (
                    <Tooltip key={dayIndex}>
                      <TooltipTrigger>
                        <div
                          className="w-3 h-3 rounded-sm transition-opacity hover:opacity-80"
                          style={{ backgroundColor: bgColor }}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs font-medium">{format(day.date, "MMM d, yyyy")}</p>
                        <p className="text-xs">{metric1Info?.name}: {val1 ?? 0}</p>
                        <p className="text-xs">{metric2Info?.name}: {val2 ?? 0}</p>
                        <p className="text-xs font-semibold mt-1">{statusText}</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            ))}
          </div>
        </TooltipProvider>
        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 text-xs text-muted-foreground mt-2">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-green-500" />
            <span>{t('compare.legend_positive')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-gray-400" />
            <span>{t('compare.legend_neutral')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-red-500" />
            <span>{t('compare.legend_negative')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-gray-200" />
            <span>{t('ui.text.no_data')}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Metric Selectors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('ui.text.first_metric')}</label>
          <Select value={metric1} onValueChange={setMetric1}>
            <SelectTrigger>
              <SelectValue placeholder={t('compare.select_metric')} />
            </SelectTrigger>
            <SelectContent>
              {availableMetrics
                .filter(m => m.key !== metric2)
                .map(m => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">{t('ui.text.second_metric')}</label>
          <Select value={metric2} onValueChange={setMetric2}>
            <SelectTrigger>
              <SelectValue placeholder={t('compare.select_metric')} />
            </SelectTrigger>
            <SelectContent>
              {availableMetrics
                .filter(m => m.key !== metric1)
                .map(m => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Graphs and Correlation */}
      {metric1 && metric2 && (
        <div className="space-y-6">
          {/* View Toggle */}
          <div className="flex justify-center">
            <Tabs value={correlationView} onValueChange={(v) => setCorrelationView(v as "traditional" | "metric-split")}>
              <TabsList>
                <TabsTrigger value="traditional">{t('ui.text.correlation_view')}</TabsTrigger>
                <TabsTrigger value="metric-split">{t('ui.text.achievement_view')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Mini Graphs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 border rounded-lg bg-card">
              {renderMiniGraph(metric1)}
            </div>
            <div className="p-4 border rounded-lg bg-card">
              {renderCorrelationGraph()}
            </div>
            <div className="p-4 border rounded-lg bg-card">
              {renderMiniGraph(metric2)}
            </div>
          </div>

          {/* Correlation Result */}
          {interpretation && (
            <div className="p-6 border rounded-lg bg-card">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-full bg-background ${interpretation.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className={`text-lg font-semibold mb-2 ${interpretation.color}`}>
                    {interpretation.text}
                  </h3>
                  {correlation !== null && (
                    <p className="text-sm text-muted-foreground mb-3">
                      Correlation coefficient: {correlation.toFixed(3)}
                    </p>
                  )}
                  {interpretation.description && (
                    <p className="text-sm text-muted-foreground">
                      {interpretation.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {!metric1 && !metric2 && (
        <div className="text-center py-12 text-muted-foreground">
          <p>{t('ui.text.select_two_metrics_to_compare_and_see_their_correl')}</p>
        </div>
      )}
    </div>
  );
};

export default CompareGraphs;
